import {STORAGE_KEYS, API_CONFIG} from '../utils/constants';
import type {ApiResponse} from '../types/api';
import { buildTrackingPayload, getSessionId } from '../utils/utm';

function getToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.TOKEN);
}

function getRefreshToken(): string | null {
  return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
}

function setToken(token: string): void {
  localStorage.setItem(STORAGE_KEYS.TOKEN, token);
}

function clearTokens(): void {
  localStorage.removeItem(STORAGE_KEYS.TOKEN);
  localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  localStorage.removeItem(STORAGE_KEYS.SESSION);
}

async function request<T = any>(
  endpoint: string,
  options: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const isFormData = options.body instanceof FormData;
  const headers: Record<string, string> = {
    ...(isFormData ? {} : {'Content-Type': 'application/json'}),
    ...(options.headers as Record<string, string> || {}),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  headers['X-Session-Id'] = getSessionId();

  const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  const url = `${baseUrl}${path}`;
  const res = await fetch(url, {...options, headers});

  const text = await res.text();
  let data: any = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    console.error('[API] Non-JSON response:', text.substring(0, 500));
    throw new Error('Server returned invalid JSON. Check console for raw response.');
  }

  if (!res.ok) {
    const errMsg = data.error || `Request failed with status ${res.status}`;
    if (res.status !== 401) {
      logClientError('ERROR', `API ${options.method || 'GET'} ${endpoint}: ${errMsg}`, {
        status: res.status,
        endpoint,
      });
    }
    if (res.status === 401 && token) {
      clearTokens();
      throw new Error('SESSION_EXPIRED');
    }
    throw new Error(errMsg);
  }

  return data as T;
}

// ═══════════════════════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════════════════════
export async function googleLogin(idToken: string, deviceId: string, platform: string, pushToken?: string, referralCode?: string) {
  return request<ApiResponse>('/auth/google', {
    method: 'POST',
    body: JSON.stringify({id_token: idToken, device_id: deviceId, platform, push_token: pushToken, referral_code: referralCode}),
  });
}

export async function refreshAccessToken(refreshToken: string) {
  return request<ApiResponse>('/auth/refresh', {
    method: 'POST',
    body: JSON.stringify({refresh_token: refreshToken}),
  });
}

export async function logout(refreshToken: string, deviceId?: string) {
  return request<ApiResponse>('/auth/logout', {
    method: 'POST',
    body: JSON.stringify({refresh_token: refreshToken, device_id: deviceId}),
  });
}

export async function acceptInvitation(token: string) {
  return request<ApiResponse>('/auth/accept-invitation', {
    method: 'POST',
    body: JSON.stringify({token}),
  });
}

// ═══════════════════════════════════════════════════════════════
// COURSES (Public)
// ═══════════════════════════════════════════════════════════════
export async function getCourses(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/courses${qs}`, {method: 'GET'});
}

export async function getCourse(slug: string) {
  return request<ApiResponse>(`/courses/${slug}`, {method: 'GET'});
}

export async function getFeaturedCourse() {
  return request<ApiResponse>('/courses/featured', {method: 'GET'});
}

export async function getBanners() {
  return request<ApiResponse>('/banners', {method: 'GET'});
}

export async function getFaq(category?: string) {
  const qs = category ? `?category=${encodeURIComponent(category)}` : '';
  return request<ApiResponse>(`/faq${qs}`, {method: 'GET'});
}

export async function getTestimonials(courseId?: number) {
  const qs = courseId ? `?course_id=${courseId}` : '';
  return request<ApiResponse>(`/testimonials${qs}`, {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// ENROLLMENTS & PAYMENTS
// ═══════════════════════════════════════════════════════════════
export async function createEnrollment(data: any) {
  return request<ApiResponse>('/enrollments', {
    method: 'POST',
    body: JSON.stringify({ ...data, ...buildTrackingPayload() }),
  });
}

export async function getEnrollment(id: number) {
  return request<ApiResponse>(`/enrollments/${id}`, {method: 'GET'});
}

export async function createPaymentOrder(courseId: number, couponCode?: string) {
  return request<ApiResponse>('/payments/create-order', {
    method: 'POST',
    body: JSON.stringify({course_id: courseId, coupon_code: couponCode, ...buildTrackingPayload()}),
  });
}

export async function verifyPayment(data: {
  razorpay_order_id: string;
  razorpay_payment_id: string;
  razorpay_signature: string;
}) {
  return request<ApiResponse>('/payments/verify', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// USER PROFILE
// ═══════════════════════════════════════════════════════════════
export async function getMe() {
  return request<ApiResponse>('/me', {method: 'GET'});
}

export async function updateMe(data: any) {
  return request<ApiResponse>('/me', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function uploadAvatar(formData: FormData) {
  const token = getToken();
  const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
  const res = await fetch(`${baseUrl}/me/avatar`, {
    method: 'POST',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
    body: formData,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
}

export async function getMyEnrollments() {
  return request<ApiResponse>('/me/enrollments', {method: 'GET'});
}

export async function getMyPayments() {
  return request<ApiResponse>('/me/payments', {method: 'GET'});
}

export async function getMyNotifications() {
  return request<ApiResponse>('/me/notifications', {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// PROGRESS
// ═══════════════════════════════════════════════════════════════
export async function trackProgress(data: {
  course_id: number;
  module_id: number;
  video_watch_time_seconds?: number;
  is_completed?: boolean;
}) {
  return request<ApiResponse>('/progress', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getCourseProgress(courseId: number) {
  return request<ApiResponse>(`/progress/courses/${courseId}`, {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// WISHLIST
// ═══════════════════════════════════════════════════════════════
export async function getWishlist() {
  return request<ApiResponse>('/wishlist', {method: 'GET'});
}

export async function addToWishlist(courseId: number) {
  return request<ApiResponse>('/wishlist', {
    method: 'POST',
    body: JSON.stringify({course_id: courseId}),
  });
}

export async function removeFromWishlist(courseId: number) {
  return request<ApiResponse>(`/wishlist/${courseId}`, {method: 'DELETE'});
}

// ═══════════════════════════════════════════════════════════════
// REVIEWS
// ═══════════════════════════════════════════════════════════════
export async function getCourseReviews(courseId: number) {
  return request<ApiResponse>(`/courses/${courseId}/reviews`, {method: 'GET'});
}

export async function createReview(data: {
  course_id: number;
  rating: number;
  title?: string;
  review?: string;
}) {
  return request<ApiResponse>('/reviews', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function markReviewHelpful(reviewId: number) {
  return request<ApiResponse>(`/reviews/${reviewId}/helpful`, {method: 'POST'});
}

// ═══════════════════════════════════════════════════════════════
// Q&A
// ═══════════════════════════════════════════════════════════════
export async function getCourseQuestions(courseId: number) {
  return request<ApiResponse>(`/courses/${courseId}/questions`, {method: 'GET'});
}

export async function askQuestion(data: {course_id: number; module_id?: number; question: string}) {
  return request<ApiResponse>('/questions', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getQuestionAnswers(questionId: number) {
  return request<ApiResponse>(`/questions/${questionId}/answers`, {method: 'GET'});
}

export async function postAnswer(data: {question_id: number; answer: string}) {
  return request<ApiResponse>('/answers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// NOTES
// ═══════════════════════════════════════════════════════════════
export async function getCourseNotes(courseId: number) {
  return request<ApiResponse>(`/notes/courses/${courseId}`, {method: 'GET'});
}

export async function createNote(data: {
  course_id: number;
  module_id: number;
  note: string;
  video_timestamp_seconds?: number;
}) {
  return request<ApiResponse>('/notes', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateNote(noteId: number, data: {note: string; video_timestamp_seconds?: number}) {
  return request<ApiResponse>(`/notes/${noteId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteNote(noteId: number) {
  return request<ApiResponse>(`/notes/${noteId}`, {method: 'DELETE'});
}

// ═══════════════════════════════════════════════════════════════
// MESSAGES
// ═══════════════════════════════════════════════════════════════
export async function getInbox() {
  return request<ApiResponse>('/messages/inbox', {method: 'GET'});
}

export async function getSentMessages() {
  return request<ApiResponse>('/messages/sent', {method: 'GET'});
}

export async function getUnreadCount() {
  return request<ApiResponse>('/messages/unread-count', {method: 'GET'});
}

export async function getConversation(otherUserId: number) {
  return request<ApiResponse>(`/messages/conversation/${otherUserId}`, {method: 'GET'});
}

export async function sendMessage(data: {
  receiver_id: number;
  course_id?: number;
  subject?: string;
  message: string;
}) {
  return request<ApiResponse>('/messages', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// SUPPORT TICKETS
// ═══════════════════════════════════════════════════════════════
export async function getTickets() {
  return request<ApiResponse>('/tickets', {method: 'GET'});
}

export async function getTicket(ticketId: number) {
  return request<ApiResponse>(`/tickets/${ticketId}`, {method: 'GET'});
}

export async function createTicket(data: {
  category?: string;
  subject: string;
  description: string;
  priority?: 'low' | 'medium' | 'high' | 'urgent';
}) {
  return request<ApiResponse>('/tickets', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function replyToTicket(ticketId: number, message: string) {
  return request<ApiResponse>(`/tickets/${ticketId}/reply`, {
    method: 'POST',
    body: JSON.stringify({message}),
  });
}

// ═══════════════════════════════════════════════════════════════
// TRAINER APPLICATION
// ═══════════════════════════════════════════════════════════════
export async function applyAsTrainer(data: {
  full_name?: string;
  email?: string;
  phone?: string;
  expertise?: string;
  bio?: string;
  sample_video_url?: string;
}) {
  return request<ApiResponse>('/trainer/apply', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getMyTrainerApplication() {
  return request<ApiResponse>('/trainer/my-application', {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// COUPONS
// ═══════════════════════════════════════════════════════════════
export async function getCourseCoupons(courseId: number) {
  return request<ApiResponse>(`/courses/${courseId}/coupons`, {method: 'GET'});
}

export async function validateCoupon(code: string, courseId?: number) {
  return request<ApiResponse>('/coupons/validate', {
    method: 'POST',
    body: JSON.stringify({code, course_id: courseId}),
  });
}

export async function createCoupon(data: {
  course_id: number;
  code: string;
  discount_type: 'percentage' | 'fixed';
  discount_value: number;
  max_uses?: number;
  valid_from?: string;
  valid_until?: string;
}) {
  return request<ApiResponse>('/coupons', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function deleteCoupon(couponId: number) {
  return request<ApiResponse>(`/coupons/${couponId}`, {method: 'DELETE'});
}

// ═══════════════════════════════════════════════════════════════
// TRAINER DASHBOARD
// ═══════════════════════════════════════════════════════════════
export async function getTrainerDashboard() {
  return request<ApiResponse>('/trainer/dashboard', {method: 'GET'});
}

export async function getTrainerCourses() {
  return request<ApiResponse>('/trainer/courses', {method: 'GET'});
}

export async function getTrainerStudents(courseId?: number) {
  const qs = courseId ? `?course_id=${courseId}` : '';
  return request<ApiResponse>(`/trainer/students${qs}`, {method: 'GET'});
}

export async function uploadTrainerMaterial(formData: FormData) {
  const token = getToken();
  const baseUrl = API_CONFIG.BASE_URL.replace(/\/$/, '');
  const res = await fetch(`${baseUrl}/trainer/materials`, {
    method: 'POST',
    headers: token ? {Authorization: `Bearer ${token}`} : {},
    body: formData,
  });
  const text = await res.text();
  const data = text ? JSON.parse(text) : {};
  if (!res.ok) throw new Error(data.error || 'Upload failed');
  return data;
}

export async function sendAnnouncement(data: {course_id?: number; title: string; message: string}) {
  return request<ApiResponse>('/trainer/announcements', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function createTrainerCourse(data: any) {
  return request<ApiResponse>('/trainer/courses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTrainerCourse(courseId: number, data: any) {
  return request<ApiResponse>(`/trainer/courses/${courseId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteTrainerCourse(courseId: number) {
  return request<ApiResponse>(`/trainer/courses/${courseId}`, {method: 'DELETE'});
}

export async function getTrainerEarnings() {
  return request<ApiResponse>('/trainer/earnings', {method: 'GET'});
}

export async function getTrainerPayouts() {
  return request<ApiResponse>('/trainer/payouts', {method: 'GET'});
}

export async function requestPayout(data: {amount: number; payment_method?: string; payment_details?: any}) {
  return request<ApiResponse>('/trainer/payouts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// ═══════════════════════════════════════════════════════════════
// LEADS
// ═══════════════════════════════════════════════════════════════
export async function captureLead(data: any) {
  return request<ApiResponse>('/leads', {
    method: 'POST',
    body: JSON.stringify({ ...data, ...buildTrackingPayload() }),
  });
}

// ═══════════════════════════════════════════════════════════════
// ADMIN APIs
// ═══════════════════════════════════════════════════════════════
export async function getAdminStats() {
  return request<ApiResponse>('/admin/stats', {method: 'GET'});
}

export async function getAdminUsers(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/users${qs}`, {method: 'GET'});
}

export async function updateUser(userId: number, data: any) {
  return request<ApiResponse>(`/admin/users/${userId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteUser(userId: number) {
  return request<ApiResponse>(`/admin/users/${userId}`, {method: 'DELETE'});
}

export async function getAdminCourses(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/courses${qs}`, {method: 'GET'});
}

export async function getAdminCourse(courseId: number) {
  return request<ApiResponse>(`/admin/courses/${courseId}`, {method: 'GET'});
}

export async function adminCreateCourse(data: any) {
  return request<ApiResponse>('/admin/courses', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function adminUpdateCourse(courseId: number, data: any) {
  return request<ApiResponse>(`/admin/courses/${courseId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function adminDeleteCourse(courseId: number) {
  return request<ApiResponse>(`/admin/courses/${courseId}`, {method: 'DELETE'});
}

export async function getAdminPayments(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/payments${qs}`, {method: 'GET'});
}

export async function updatePayment(paymentId: number, data: any) {
  return request<ApiResponse>(`/admin/payments/${paymentId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getAdminEnrollments(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/enrollments${qs}`, {method: 'GET'});
}

export async function getAdminLeads(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/leads${qs}`, {method: 'GET'});
}

export async function updateLead(leadId: number, data: any) {
  return request<ApiResponse>(`/admin/leads/${leadId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteLead(leadId: number) {
  return request<ApiResponse>(`/admin/leads/${leadId}`, {method: 'DELETE'});
}

export async function getCategories() {
  return request<ApiResponse>('/admin/categories', {method: 'GET'});
}

export async function createCategory(data: any) {
  return request<ApiResponse>('/admin/categories', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getTrainers() {
  return request<ApiResponse>('/admin/trainers', {method: 'GET'});
}

export async function createTrainer(data: any) {
  return request<ApiResponse>('/admin/trainers', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateTrainer(trainerId: number, data: any) {
  return request<ApiResponse>(`/admin/trainers/${trainerId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getCms(type: string) {
  return request<ApiResponse>(`/admin/cms/${type}`, {method: 'GET'});
}

export async function updateCms(type: string, data: any) {
  return request<ApiResponse>(`/admin/cms/${type}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Admin: Trainer Applications
export async function getTrainerApplications(status?: string) {
  const qs = status ? `?status=${status}` : '';
  return request<ApiResponse>(`/admin/trainer-applications${qs}`, {method: 'GET'});
}

export async function reviewTrainerApplication(applicationId: number, data: {status: 'approved' | 'rejected'; review_notes?: string}) {
  return request<ApiResponse>(`/admin/trainer-applications/${applicationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Admin: Review Moderation
export async function getPendingReviews() {
  return request<ApiResponse>('/admin/reviews/pending', {method: 'GET'});
}

export async function approveReview(reviewId: number) {
  return request<ApiResponse>(`/admin/reviews/${reviewId}/approve`, {method: 'PUT'});
}

export async function deleteReview(reviewId: number) {
  return request<ApiResponse>(`/admin/reviews/${reviewId}`, {method: 'DELETE'});
}

// Admin: Support Tickets
export async function updateTicket(ticketId: number, data: any) {
  return request<ApiResponse>(`/admin/tickets/${ticketId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Admin: Content Flags
export async function getContentFlags(status?: string) {
  const qs = status ? `?status=${status}` : '';
  return request<ApiResponse>(`/admin/content-flags${qs}`, {method: 'GET'});
}

export async function updateContentFlag(flagId: number, data: {status: 'reviewed' | 'dismissed'}) {
  return request<ApiResponse>(`/admin/content-flags/${flagId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Admin: Payouts
export async function getAdminPayouts(status?: string) {
  const qs = status ? `?status=${status}` : '';
  return request<ApiResponse>(`/admin/payouts${qs}`, {method: 'GET'});
}

export async function updatePayout(payoutId: number, data: {status: 'processing' | 'paid' | 'rejected'}) {
  return request<ApiResponse>(`/admin/payouts/${payoutId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// System Settings (public)
export async function getPublicSettings(group?: string) {
  const params = group ? `?group=${encodeURIComponent(group)}` : '';
  return request<ApiResponse>(`/settings${params}`, {method: 'GET'});
}

export async function getPublicSettingsBulk(keys: string[]) {
  return request<ApiResponse>(`/settings/bulk?keys=${encodeURIComponent(keys.join(','))}`, {method: 'GET'});
}

// Admin: System Settings
export async function updateSystemSettings(data: {settings: Array<{key: string; value: string; group_name?: string; is_public?: boolean}>}) {
  return request<ApiResponse>('/settings', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// Admin: Invitations
export async function inviteAdmin(data: {email: string; role?: string; permissions?: any}) {
  return request<ApiResponse>('/admin/invitations', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

// Admin: Error Logs
export async function getAdminLogs(params?: {page?: number; per_page?: number; level?: string; search?: string; from?: string; to?: string}) {
  const qs = new URLSearchParams();
  if (params?.page) qs.set('page', String(params.page));
  if (params?.per_page) qs.set('per_page', String(params.per_page));
  if (params?.level) qs.set('level', params.level);
  if (params?.search) qs.set('search', params.search);
  if (params?.from) qs.set('from', params.from);
  if (params?.to) qs.set('to', params.to);
  return request<ApiResponse>(`/admin/logs?${qs.toString()}`, {method: 'GET'});
}

export async function getAdminLogStats() {
  return request<ApiResponse>('/admin/logs/stats', {method: 'GET'});
}

export async function clearAdminLogs(params?: {level?: string; older_than_days?: number}) {
  const qs = new URLSearchParams();
  if (params?.level) qs.set('level', params.level);
  if (params?.older_than_days) qs.set('older_than_days', String(params.older_than_days));
  return request<ApiResponse>(`/admin/logs?${qs.toString()}`, {method: 'DELETE'});
}

// Client Error Logging
export async function logClientError(level: string, message: string, context?: any) {
  try {
    await fetch(`${API_CONFIG.BASE_URL}/client-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ level, message, context: context || {}, route: window.location.href }),
    });
  } catch {
    // silent fail — don't infinite loop on logging failure
  }
}

// Course Modules (Admin / Trainer)
export async function getCourseModules(courseId: number) {
  return request<ApiResponse>(`/admin/courses/${courseId}/modules`, {method: 'GET'});
}

export async function createCourseModule(courseId: number, data: any) {
  return request<ApiResponse>(`/admin/courses/${courseId}/modules`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCourseModule(courseId: number, moduleId: number, data: any) {
  return request<ApiResponse>(`/admin/courses/${courseId}/modules/${moduleId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCourseModule(courseId: number, moduleId: number) {
  return request<ApiResponse>(`/admin/courses/${courseId}/modules/${moduleId}`, {method: 'DELETE'});
}

// Course FAQ (Admin / Trainer)
export async function getCourseFaq(courseId: number) {
  return request<ApiResponse>(`/admin/courses/${courseId}/faq`, {method: 'GET'});
}

export async function createCourseFaq(courseId: number, data: any) {
  return request<ApiResponse>(`/admin/courses/${courseId}/faq`, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateCourseFaq(courseId: number, faqId: number, data: any) {
  return request<ApiResponse>(`/admin/courses/${courseId}/faq/${faqId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteCourseFaq(courseId: number, faqId: number) {
  return request<ApiResponse>(`/admin/courses/${courseId}/faq/${faqId}`, {method: 'DELETE'});
}

// ═══════════════════════════════════════════════════════════════
// MARKETING ANALYTICS
// ═══════════════════════════════════════════════════════════════
export async function getMarketingOverview(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/marketing/overview${qs}`, {method: 'GET'});
}

export async function getMarketingFunnel(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/marketing/funnel${qs}`, {method: 'GET'});
}

export async function getMarketingChannels(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/marketing/channels${qs}`, {method: 'GET'});
}

export async function getMarketingCampaigns(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/marketing/campaigns${qs}`, {method: 'GET'});
}

export async function getMarketingCoupons(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/marketing/coupons${qs}`, {method: 'GET'});
}

export async function getMarketingTrends(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/marketing/trends${qs}`, {method: 'GET'});
}

export async function trackAbandonedCheckout(data: any) {
  return request<ApiResponse>('/abandoned-checkouts', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAdminAbandonedCheckouts(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/abandoned-checkouts${qs}`, {method: 'GET'});
}

export async function recoverAbandonedCheckout(id: number) {
  return request<ApiResponse>(`/admin/abandoned-checkouts/${id}/recover`, {method: 'POST'});
}

// ═══════════════════════════════════════════════════════════════
// MENTOR (AI CAREER COMPANION)
// ═══════════════════════════════════════════════════════════════
export async function mentorChatPublic(data: {message: string; history: Array<{role: string; content: string}>}) {
  return request<ApiResponse>('/mentor/chat/public', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function mentorChat(data: {message: string; conversation_id?: number}) {
  return request<ApiResponse>('/mentor/chat', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getMentorConversations() {
  return request<ApiResponse>('/mentor/conversations', {method: 'GET'});
}

export async function getMentorConversation(conversationId: number) {
  return request<ApiResponse>(`/mentor/conversations/${conversationId}`, {method: 'GET'});
}

export async function updateMentorGoal(data: {
  target_role?: string;
  current_role?: string;
  years_experience?: number;
  career_goal_summary?: string;
}) {
  return request<ApiResponse>('/mentor/goal', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getMentorGapAnalysis() {
  return request<ApiResponse>('/mentor/gap-analysis', {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// WORKSHOPS
// ═══════════════════════════════════════════════════════════════
export async function getAdminWorkshops(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/workshops${qs}`, {method: 'GET'});
}

export async function getAdminWorkshop(workshopId: number) {
  return request<ApiResponse>(`/admin/workshops/${workshopId}`, {method: 'GET'});
}

export async function getUpcomingWorkshops() {
  return request<ApiResponse>('/workshops/upcoming', {method: 'GET'});
}

export async function getWorkshop(workshopId: number) {
  return request<ApiResponse>(`/workshops/${workshopId}`, {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// REFERRAL SYSTEM
// ═══════════════════════════════════════════════════════════════
export async function validateReferralCode(code: string) {
  return request<ApiResponse>(`/referral/validate?code=${encodeURIComponent(code)}`, {method: 'GET'});
}

export async function getMyReferral() {
  return request<ApiResponse>('/me/referral', {method: 'GET'});
}

export async function applyForReferral(data: { reason: string }) {
  return request<ApiResponse>('/me/referral/apply', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getMyReferralEarnings() {
  return request<ApiResponse>('/me/referral/earnings', {method: 'GET'});
}

export async function requestReferralPayout(data: { amount: number; payment_method: string; payment_details: any }) {
  return request<ApiResponse>('/me/referral/payout', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAdminReferralApplications(status?: string) {
  const qs = status ? `?status=${encodeURIComponent(status)}` : '';
  return request<ApiResponse>(`/admin/referral-applications${qs}`, {method: 'GET'});
}

export async function reviewReferralApplication(applicationId: number, data: { status: string; review_notes?: string }) {
  return request<ApiResponse>(`/admin/referral-applications/${applicationId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function getAdminReferrals(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/referrals${qs}`, {method: 'GET'});
}

export async function getAdminReferralEarnings(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/referral-earnings${qs}`, {method: 'GET'});
}

export async function getAdminReferralPayouts(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/referral-payouts${qs}`, {method: 'GET'});
}

export async function processReferralPayout(payoutId: number, data: { status: string }) {
  return request<ApiResponse>(`/admin/referral-payouts/${payoutId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function createWorkshop(data: any) {
  return request<ApiResponse>('/admin/workshops', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWorkshop(workshopId: number, data: any) {
  return request<ApiResponse>(`/admin/workshops/${workshopId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWorkshop(workshopId: number) {
  return request<ApiResponse>(`/admin/workshops/${workshopId}`, {method: 'DELETE'});
}

// ═══════════════════════════════════════════════════════════════
// DAILY NEWS (PUBLIC)
// ═══════════════════════════════════════════════════════════════
export async function getNews(params?: { date?: string; category?: string; page?: number; per_page?: number }) {
  const qs = new URLSearchParams();
  if (params?.date) qs.set('date', params.date);
  if (params?.category) qs.set('category', params.category);
  if (params?.page) qs.set('page', String(params.page));
  if (params?.per_page) qs.set('per_page', String(params.per_page));
  return request<ApiResponse>(`/news?${qs.toString()}`, {method: 'GET'});
}

export async function getNewsItem(id: number) {
  return request<ApiResponse>(`/news/${id}`, {method: 'GET'});
}

export async function getNewsDates() {
  return request<ApiResponse>('/news/dates', {method: 'GET'});
}

export async function getNewsCategories() {
  return request<ApiResponse>('/news/categories', {method: 'GET'});
}

// ═══════════════════════════════════════════════════════════════
// BLOGS (ADMIN)
// ═══════════════════════════════════════════════════════════════
export async function getAdminBlogs(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/blogs${qs}`, {method: 'GET'});
}

export async function createBlog(data: any) {
  return request<ApiResponse>('/admin/blogs', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateBlog(blogId: number, data: any) {
  return request<ApiResponse>(`/admin/blogs/${blogId}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteBlog(blogId: number) {
  return request<ApiResponse>(`/admin/blogs/${blogId}`, {method: 'DELETE'});
}

// ═══════════════════════════════════════════════════════════════
// AI CONTENT GENERATION
// ═══════════════════════════════════════════════════════════════
export async function generateCourseAI(topic: string) {
  return request<ApiResponse>('/admin/ai/generate-course', {
    method: 'POST',
    body: JSON.stringify({ topic }),
  });
}

export async function generateWorkshopAI(topic: string) {
  return request<ApiResponse>('/admin/ai/generate-workshop', {
    method: 'POST',
    body: JSON.stringify({ topic }),
  });
}

export async function generateBlogAI(topic: string) {
  return request<ApiResponse>('/admin/ai/generate-blog', {
    method: 'POST',
    body: JSON.stringify({ topic }),
  });
}

// ═══════════════════════════════════════════════════════════════
// CERTIFICATES
// ═══════════════════════════════════════════════════════════════
export async function getMyCertificates() {
  return request<ApiResponse>('/certificates');
}

export async function getCertificate(id: number) {
  return request<ApiResponse>(`/certificates/${id}`);
}

export async function generateCertificates(data: { course_id?: number; workshop_id?: number; signature_url?: string }) {
  return request<ApiResponse>('/admin/certificates/generate', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getAdminCertificates(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/certificates${qs}`);
}

export async function revokeCertificate(id: number) {
  return request<ApiResponse>(`/admin/certificates/${id}/revoke`, { method: 'PUT' });
}

export async function uploadSignature(file: File) {
  const formData = new FormData();
  formData.append('signature', file);
  return request<ApiResponse>('/admin/certificates/signature', {
    method: 'POST',
    body: formData,
  });
}

// Backward-compatible aliases
export const createCourse = adminCreateCourse;
export const updateCourse = adminUpdateCourse;
export const deleteCourse = adminDeleteCourse;

export {getToken, getRefreshToken, setToken, clearTokens};

// ═══════════════════════════════════════════════════════════════
// EMAIL ADMIN
// ═══════════════════════════════════════════════════════════════
export async function getAdminEmailLogs(page?: number, search?: string, template?: string, status?: string) {
  const params = new URLSearchParams();
  if (page) params.set('page', String(page));
  if (search) params.set('search', search);
  if (template) params.set('template', template);
  if (status) params.set('status', status);
  const qs = params.toString();
  return request<ApiResponse>(`/admin/emails${qs ? `?${qs}` : ''}`);
}

export async function getAdminEmailTemplates() {
  return request<ApiResponse>('/admin/email-templates');
}

export async function sendAdminEmail(email: string, name: string, subject: string, message: string) {
  return request<ApiResponse>('/admin/send-email', {
    method: 'POST',
    body: JSON.stringify({ email, name, subject, message }),
  });
}

// ═══════════════════════════════════════════════════════════════
// WHATSAPP ADMIN
// ═══════════════════════════════════════════════════════════════
export async function getWaBalance() {
  return request<ApiResponse>('/admin/whatsapp/balance');
}

export async function getWaStats(days?: number) {
  const qs = days ? `?days=${days}` : '';
  return request<ApiResponse>(`/admin/whatsapp/stats${qs}`);
}

// Templates
export async function getWaTemplates() {
  return request<ApiResponse>('/admin/whatsapp/templates');
}

export async function createWaTemplate(data: any) {
  return request<ApiResponse>('/admin/whatsapp/templates', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWaTemplate(id: number, data: any) {
  return request<ApiResponse>(`/admin/whatsapp/templates/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWaTemplate(id: number) {
  return request<ApiResponse>(`/admin/whatsapp/templates/${id}`, { method: 'DELETE' });
}

// Flows
export async function getWaFlows() {
  return request<ApiResponse>('/admin/whatsapp/flows');
}

export async function getWaFlow(id: number) {
  return request<ApiResponse>(`/admin/whatsapp/flows/${id}`);
}

export async function createWaFlow(data: any) {
  return request<ApiResponse>('/admin/whatsapp/flows', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWaFlow(id: number, data: any) {
  return request<ApiResponse>(`/admin/whatsapp/flows/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWaFlow(id: number) {
  return request<ApiResponse>(`/admin/whatsapp/flows/${id}`, { method: 'DELETE' });
}

export async function saveWaFlowSteps(id: number, steps: any[]) {
  return request<ApiResponse>(`/admin/whatsapp/flows/${id}/steps`, {
    method: 'POST',
    body: JSON.stringify({ steps }),
  });
}

export async function seedWaDefaultFlows() {
  return request<ApiResponse>('/admin/whatsapp/flows/seed-defaults', { method: 'POST' });
}

// Auto-replies
export async function getWaAutoReplies() {
  return request<ApiResponse>('/admin/whatsapp/auto-replies');
}

export async function createWaAutoReply(data: any) {
  return request<ApiResponse>('/admin/whatsapp/auto-replies', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function updateWaAutoReply(id: number, data: any) {
  return request<ApiResponse>(`/admin/whatsapp/auto-replies/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function deleteWaAutoReply(id: number) {
  return request<ApiResponse>(`/admin/whatsapp/auto-replies/${id}`, { method: 'DELETE' });
}

// Contacts
export async function getWaContacts(params?: Record<string, string>) {
  const qs = params ? '?' + new URLSearchParams(params).toString() : '';
  return request<ApiResponse>(`/admin/whatsapp/contacts${qs}`);
}

export async function updateWaContact(id: number, data: any) {
  return request<ApiResponse>(`/admin/whatsapp/contacts/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function bulkTagWaContacts(data: { ids: number[]; tag: string; action: 'add' | 'remove' }) {
  return request<ApiResponse>('/admin/whatsapp/contacts/bulk-tag', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function getWaContactsByTag(tag: string) {
  return request<ApiResponse>(`/admin/whatsapp/contacts/tag/${tag}`);
}

// Scheduled
export async function getWaScheduled() {
  return request<ApiResponse>('/admin/whatsapp/scheduled');
}

export async function createWaScheduled(data: any) {
  return request<ApiResponse>('/admin/whatsapp/scheduled', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function cancelWaScheduled(id: number) {
  return request<ApiResponse>(`/admin/whatsapp/scheduled/${id}/cancel`, { method: 'POST' });
}

export async function processWaScheduled() {
  return request<ApiResponse>('/admin/whatsapp/scheduled/process', { method: 'POST' });
}

// Inbox
export async function getWaThreads() {
  return request<ApiResponse>('/admin/whatsapp/threads');
}

export async function getWaMessages(params: { phone: string }) {
  return request<ApiResponse>(`/admin/whatsapp/messages?phone=${encodeURIComponent(params.phone)}`);
}

export async function sendWaMessage(phone: string, message: string) {
  return request<ApiResponse>('/admin/whatsapp/messages', {
    method: 'POST',
    body: JSON.stringify({ phone, message }),
  });
}

export async function markWaRead(phone: string) {
  return request<ApiResponse>('/admin/whatsapp/messages/read', {
    method: 'POST',
    body: JSON.stringify({ phone }),
  });
}

// Broadcast
export async function sendWaBroadcast(data: any) {
  return request<ApiResponse>('/admin/whatsapp/broadcast', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}
